import { useState, useEffect } from "react";

export const useLocalStorage = (key, initialValue) => {
	// Get initial value from localStorage or use initialValue if not found
	const [value, setValue] = useState(() => {
		try {
			const stored = localStorage.getItem(key);
			return stored !== null ? JSON.parse(stored) : initialValue;
		} catch (error) {
			console.error(`Error reading localStorage key "${key}":`, error);
			return initialValue;
		}
	});

	// Update localStorage when value changes
	useEffect(() => {
		try {
			// Store value in localStorage
			localStorage.setItem(key, JSON.stringify(value));

			// Dispatch a custom event so other components can react to this change
			window.dispatchEvent(
				new StorageEvent("storage", {
					key,
					newValue: JSON.stringify(value),
					storageArea: localStorage,
				}),
			);
		} catch (error) {
			console.error(`Error writing to localStorage key "${key}":`, error);
		}
	}, [key, value]);

	// Listen for changes from other components
	useEffect(() => {
		const handleStorageChange = (event) => {
			if (event.key === key && event.storageArea === localStorage) {
				try {
					// Only update state if the value is different
					const newValue =
						event.newValue !== null ? JSON.parse(event.newValue) : initialValue;

					// Use functional update to compare with current state
					setValue((currentValue) => {
						// Only update if the values are different
						const isDifferent =
							JSON.stringify(currentValue) !== JSON.stringify(newValue);
						return isDifferent ? newValue : currentValue;
					});
				} catch (error) {
					console.error(
						`Error handling storage event for key "${key}":`,
						error,
					);
				}
			}
		};

		// Add event listener for storage events
		window.addEventListener("storage", handleStorageChange);

		// Remove event listener on cleanup
		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, [key, initialValue]);

	return [value, setValue];
};
